import { type CSSProperties, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  ABILITIES,
  buildChartMonths,
  buildSmoothPath,
  findBestTimeWindow,
  getCrowdLevel,
  hasWaveSizeData,
  MONTH_ABBR,
  isShoulderSeasonMonth,
  type Ability,
  type ChartMonth,
  type CrowdLevel,
  type MonthlyIntelligenceRow,
} from '@/lib/destination-monthly-intelligence';

interface SeasonAbilityGraphProps {
  monthlyData: MonthlyIntelligenceRow[];
  className?: string;
}

// One fixed logical composition is uniformly scaled to its available width.
// No child has its own responsive breakpoint or independent fluid size.
const CANVAS_WIDTH = 720;
const CANVAS_HEIGHT = 330;
const CANVAS_PADDING = 14;
const PLOT_LEFT = 14;
const PLOT_WIDTH = CANVAS_WIDTH - CANVAS_PADDING * 2;
const MONTH_GRID = 'grid grid-cols-12';
const GRID_GAP = { columnGap: '3px' } as const;

/**
 * Consistency is stored as a researched 1-10 judgement score
 * (surf_consistency_score_1_10), NOT a measured probability of surfable days.
 * It is therefore rendered as "n/10" and deliberately never multiplied into a
 * percentage. Swap this single formatter once a real percentage metric exists.
 */
const formatConsistency = (score: number | null): string => (score === null ? '–' : `${score}/10`);

// Teal intensity ramp for personal suitability (1-10 -> subtle -> strong).
const SUITABILITY_OPACITY = [0.08, 0.13, 0.19, 0.26, 0.34, 0.44, 0.56, 0.70, 0.85, 1] as const;

const stepIndex = (score: number, length: number) =>
  Math.min(length - 1, Math.max(0, Math.round((score / 10) * (length - 1))));

// Restrained height variation: the silhouette of the year should read at a
// glance without becoming a conventional bar chart.
const MIN_FILL_PCT = 55;
const fillPct = (score: number | null) =>
  score === null ? 100 : MIN_FILL_PCT + ((score - 1) / 9) * (100 - MIN_FILL_PCT);

// Micro-caption naming the row that follows, so a bare "2.2" or "9/10" is never
// ambiguous without a legend.
const RowCaption = ({ children, tone, style }: {
  children: string;
  tone: 'wave' | 'quiet';
  style?: CSSProperties;
}) => (
  <div
    className={cn(
      'uppercase tracking-[0.12em] leading-none',
      tone === 'wave' ? 'text-primary/80' : 'text-muted-foreground',
    )}
    style={{ fontSize: 9, ...style }}
  >
    {children}
  </div>
);

const WaveCurve = ({ chartMonths }: { chartMonths: ChartMonth[] }) => {
  // Plotted in a 12-unit viewBox so each point sits at its own month column's centre.
  const width = 12;
  const height = 44;
  const values = chartMonths.map((m) => m.waveHeightM);
  const numericValues = values.filter((v): v is number => v !== null);
  const min = Math.min(...numericValues);
  const rawMax = Math.max(...numericValues);
  const max = rawMax === min ? min + 1 : rawMax;
  const top = 4;
  const plotHeight = height - top - 4;
  const path = buildSmoothPath(values, width, plotHeight, min, max);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="h-full w-full"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <g transform={`translate(0.5 ${top})`}>
        <path
          d={`${path} L ${width - 1} ${plotHeight} L 0 ${plotHeight} Z`}
          className="fill-primary/10"
          stroke="none"
        />
        <path
          d={path}
          fill="none"
          className="stroke-primary"
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
};

const formatMetric = (value: number | null, suffix: string): string =>
  value !== null ? `${value}${suffix}` : 'Not available';

const CROWD_LABEL: Record<CrowdLevel, string> = {
  quiet: 'Quiet',
  moderate: 'Moderate',
  busy: 'Busy',
  unknown: 'Not available',
};

const formatCrowd = (score: number | null): string => {
  const level = getCrowdLevel(score);
  return level === 'unknown' ? 'Not available' : `${CROWD_LABEL[level]} · ${score}/10`;
};

const MonthDetailBody = ({ chartMonth, ability }: { chartMonth: ChartMonth; ability: Ability }) => {
  const row = chartMonth.row;
  if (!row) {
    return <p className="text-sm text-muted-foreground">No detail available for this month yet.</p>;
  }
  const abilityLabel = ABILITIES.find((a) => a.value === ability)?.label.toLowerCase();
  const isShoulder = isShoulderSeasonMonth(row);

  return (
    <div>
      {chartMonth.suitability !== null && (
        <div className="flex justify-between gap-2 border-b border-border pb-2 mb-2">
          <span className="text-muted-foreground">For {abilityLabel}s</span>
          <span className="font-medium text-suitability tabular-nums">{chartMonth.suitability}/10</span>
        </div>
      )}
      {isShoulder && (
        <p className="mb-2 text-xs font-medium text-primary">Strong shoulder-season value this month</p>
      )}
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <div className="col-span-2 sm:col-span-1 flex justify-between gap-2">
          <dt className="text-muted-foreground">Wave height</dt>
          <dd className="text-foreground tabular-nums">{formatMetric(row.typicalBreakingWaveFaceHeightM, 'm')}</dd>
        </div>
        <div className="col-span-2 sm:col-span-1 flex justify-between gap-2">
          <dt className="text-muted-foreground">Surfable days</dt>
          <dd className="text-foreground tabular-nums">{formatMetric(row.surfableDaysPct, '%')}</dd>
        </div>
        <div className="col-span-2 sm:col-span-1 flex justify-between gap-2">
          <dt className="text-muted-foreground">Reliability</dt>
          <dd className="text-foreground tabular-nums">{formatMetric(row.surfConsistencyScore, '/10')}</dd>
        </div>
        <div className="col-span-2 sm:col-span-1 flex justify-between gap-2">
          <dt className="text-muted-foreground">Crowds</dt>
          <dd className="text-foreground tabular-nums">{formatCrowd(row.crowdPressureScore)}</dd>
        </div>
        <div className="col-span-2 sm:col-span-1 flex justify-between gap-2">
          <dt className="text-muted-foreground">Water temp</dt>
          <dd className="text-foreground tabular-nums">{formatMetric(row.meanWaterTemperatureC, '°C')}</dd>
        </div>
        <div className="col-span-2 sm:col-span-1 flex justify-between gap-2">
          <dt className="text-muted-foreground">Weather</dt>
          <dd className="text-foreground tabular-nums">
            {row.meanDailyLowC !== null && row.meanDailyHighC !== null
              ? `${row.meanDailyLowC}–${row.meanDailyHighC}°C`
              : 'Not available'}
          </dd>
        </div>
        <div className="col-span-2 sm:col-span-1 flex justify-between gap-2">
          <dt className="text-muted-foreground">Rain</dt>
          <dd className="text-foreground tabular-nums">
            {row.rainDays !== null ? `${row.rainDays} wet days` : formatMetric(row.monthlyRainfallMm, 'mm')}
          </dd>
        </div>
        <div className="col-span-2 sm:col-span-1 flex justify-between gap-2">
          <dt className="text-muted-foreground">Comfort</dt>
          <dd className="text-foreground tabular-nums">{formatMetric(row.comfortScore, '/10')}</dd>
        </div>
        <div className="col-span-2 sm:col-span-1 flex justify-between gap-2">
          <dt className="text-muted-foreground">Wind</dt>
          <dd className="text-foreground text-right">{row.prevailingWindDirection ?? 'Not available'}</dd>
        </div>
      </dl>
    </div>
  );
};

const SeasonAbilityGraph = ({ monthlyData, className }: SeasonAbilityGraphProps) => {
  const isMobile = useIsMobile();
  const scaleHostRef = useRef<HTMLDivElement>(null);
  const [canvasScale, setCanvasScale] = useState(1);
  const [ability, setAbility] = useState<Ability>('intermediate');
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);

  const chartMonths = useMemo(() => buildChartMonths(monthlyData, ability), [monthlyData, ability]);
  const showWaveHeight = useMemo(() => hasWaveSizeData(chartMonths), [chartMonths]);
  const bestWindow = useMemo(() => findBestTimeWindow(chartMonths), [chartMonths]);
  const selected = chartMonths.find((m) => m.month === selectedMonth) ?? null;

  useLayoutEffect(() => {
    const host = scaleHostRef.current;
    if (!host) return;

    const updateScale = () => setCanvasScale(host.clientWidth / CANVAS_WIDTH);
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  if (monthlyData.length === 0) {
    return (
      <div className={cn('border border-border px-4 py-10 text-center', className)}>
        <p className="text-sm text-muted-foreground">
          Season-by-season detail isn't available for this destination yet.
        </p>
      </div>
    );
  }

  // Mobile gets an intentionally separate layout -- not the desktop canvas
  // scaled down. Real responsive Tailwind sizing, taller touch targets, and a
  // document-flow layout instead of one fixed 720px composition shrunk to fit.
  if (isMobile) {
    return (
      <div className={className}>
        <div>
          <h3 className="font-serif text-xl font-semibold leading-tight text-foreground">When should you go?</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            See how the surf changes through the year — and when it suits you best.
          </p>
        </div>

        <div className="mt-4 grid grid-cols-4 gap-1.5" role="group" aria-label="Select surfing ability">
          {ABILITIES.map((a) => (
            <button
              key={a.value}
              type="button"
              onClick={() => setAbility(a.value)}
              aria-pressed={ability === a.value}
              className={cn(
                'min-h-11 rounded-lg px-1 py-2 text-[11px] font-semibold uppercase leading-tight tracking-tight transition-colors',
                ability === a.value ? 'bg-foreground text-background' : 'bg-muted/60 text-muted-foreground',
              )}
            >
              {a.label}
            </button>
          ))}
        </div>

        <div className="mt-4 rounded-xl border border-border bg-background px-1 py-3 sm:px-3">
          <div className={cn(MONTH_GRID, 'gap-x-0')}>
            {chartMonths.map((m) => (
              <div
                key={m.month}
                className={cn(
                  'text-center text-[9px] font-semibold uppercase leading-none sm:text-[11px]',
                  m.month === selectedMonth ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {m.monthLabel}
              </div>
            ))}
          </div>

          {showWaveHeight && (
            <div className="mt-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-primary/80">Wave height (m)</div>
              <div className={cn(MONTH_GRID, 'mt-1 gap-x-0')}>
                {chartMonths.map((m) => (
                  <div key={m.month} className="text-center text-[9px] font-medium leading-none tabular-nums text-primary/90 sm:text-[10px]">
                    {m.waveHeightM !== null ? m.waveHeightM : '–'}
                  </div>
                ))}
              </div>
              <div className="mt-1.5 h-12">
                <WaveCurve chartMonths={chartMonths} />
              </div>
            </div>
          )}

          <div className="mt-3">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Consistency (out of 10)</div>
            <div className={cn(MONTH_GRID, 'mt-1 gap-x-0')}>
              {chartMonths.map((m) => (
                <div key={m.month} className="text-center text-[9px] leading-none tabular-nums text-muted-foreground sm:text-[10px]">
                  {m.consistency ?? '–'}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Your fit</div>
            <div className={cn(MONTH_GRID, 'mt-1 gap-x-0.5')}>
              {chartMonths.map((m) => {
                const isSelected = m.month === selectedMonth;
                const strong = m.suitability !== null && m.suitability >= 8;
                return (
                  <button
                    key={m.month}
                    type="button"
                    onClick={() => setSelectedMonth(isSelected ? null : m.month)}
                    aria-pressed={isSelected}
                    aria-label={`${m.monthLabel} details`}
                    className={cn(
                      'flex h-20 items-end justify-center rounded-sm transition-colors',
                      isSelected && 'outline outline-2 outline-offset-1 outline-foreground',
                    )}
                    style={{ backgroundColor: 'hsl(var(--foreground) / 0.03)' }}
                  >
                    <span
                      className={cn(
                        'flex w-full items-center justify-center rounded-sm text-sm font-bold tabular-nums',
                        m.suitability === null
                          ? 'text-muted-foreground'
                          : strong
                            ? 'text-suitability-foreground'
                            : 'text-foreground',
                      )}
                      style={{
                        height: `${fillPct(m.suitability)}%`,
                        backgroundColor:
                          m.suitability === null
                            ? 'transparent'
                            : `hsl(var(--suitability) / ${SUITABILITY_OPACITY[stepIndex(m.suitability, SUITABILITY_OPACITY.length)]})`,
                      }}
                    >
                      {m.suitability === null ? '–' : m.suitability}
                    </span>
                  </button>
                );
              })}
            </div>
            {bestWindow && (
              <div className="relative mt-1 h-4">
                <div
                  className="absolute inset-y-0 flex flex-col items-center gap-0.5"
                  style={{
                    left: `${((bestWindow.startMonth - 1) / 12) * 100}%`,
                    width: `${((bestWindow.endMonth - bestWindow.startMonth + 1) / 12) * 100}%`,
                  }}
                >
                  <div className="h-px w-full bg-suitability" />
                  <span className="whitespace-nowrap text-[9px] font-semibold uppercase tracking-[0.06em] text-suitability">
                    Best time
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        <Sheet open={selected !== null} onOpenChange={(open) => !open && setSelectedMonth(null)}>
          <SheetContent side="bottom" className="rounded-t-md">
            <SheetHeader className="text-left">
              <SheetTitle className="font-serif text-xl">{selected?.monthLabel ?? ''}</SheetTitle>
            </SheetHeader>
            <div className="mt-4">{selected && <MonthDetailBody chartMonth={selected} ability={ability} />}</div>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        ref={scaleHostRef}
        className="relative w-full overflow-visible"
        style={{ height: CANVAS_HEIGHT * canvasScale }}
      >
        <div
          className="absolute left-0 top-0 origin-top-left border border-border bg-background"
          style={{
            width: CANVAS_WIDTH,
            height: CANVAS_HEIGHT,
            transform: `scale(${canvasScale})`,
          }}
        >
          <div className="absolute" style={{ left: CANVAS_PADDING, top: 12 }}>
            <h3 className="font-serif leading-none text-foreground" style={{ fontSize: 22 }}>
              When should you go?
            </h3>
            <p className="mt-1 leading-none text-muted-foreground" style={{ fontSize: 10 }}>
              See how the surf changes through the year — and when it suits you best.
            </p>
          </div>

          <div
            className="absolute grid grid-cols-4 divide-x divide-border border border-border"
            role="group"
            aria-label="Select surfing ability"
            style={{ left: 366, top: 12, width: 340, height: 34 }}
          >
            {ABILITIES.map((a) => (
              <button
                key={a.value}
                type="button"
                onClick={() => setAbility(a.value)}
                aria-pressed={ability === a.value}
                className={cn(
                  'relative flex items-center justify-center whitespace-nowrap font-medium uppercase leading-none tracking-[0.03em] transition-colors after:absolute after:left-0 after:top-1/2 after:h-[88px] after:w-full after:-translate-y-1/2 after:content-[\'\']',
                  ability === a.value
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                style={{ fontSize: 11 }}
              >
                {a.label}
              </button>
            ))}
          </div>

          <div className={cn(MONTH_GRID, 'absolute')} style={{ ...GRID_GAP, left: PLOT_LEFT, top: 66, width: PLOT_WIDTH }}>
            {chartMonths.map((m) => (
              <div
                key={m.month}
                className={cn(
                  'text-center uppercase leading-none tracking-[0.02em]',
                  m.month === selectedMonth ? 'font-semibold text-foreground' : 'text-muted-foreground',
                )}
                style={{ fontSize: 12 }}
              >
                {m.monthLabel}
              </div>
            ))}
          </div>

          <RowCaption tone="wave" style={{ position: 'absolute', left: PLOT_LEFT, top: 86 }}>Wave height</RowCaption>
          <div className={cn(MONTH_GRID, 'absolute')} style={{ ...GRID_GAP, left: PLOT_LEFT, top: 100, width: PLOT_WIDTH }}>
            {chartMonths.map((m) => (
              <div key={m.month} className="text-center leading-none tabular-nums text-primary/90" style={{ fontSize: 11 }}>
                {showWaveHeight && m.waveHeightM !== null ? `${m.waveHeightM}m` : '–'}
              </div>
            ))}
          </div>

          {showWaveHeight && (
            <div className="absolute" style={{ left: PLOT_LEFT, top: 116, width: PLOT_WIDTH, height: 62 }}>
              <WaveCurve chartMonths={chartMonths} />
            </div>
          )}

          <RowCaption tone="quiet" style={{ position: 'absolute', left: PLOT_LEFT, top: 184 }}>Consistency</RowCaption>
          <div className={cn(MONTH_GRID, 'absolute')} style={{ ...GRID_GAP, left: PLOT_LEFT, top: 198, width: PLOT_WIDTH }}>
            {chartMonths.map((m) => (
              <div key={m.month} className="text-center leading-none tabular-nums text-muted-foreground" style={{ fontSize: 10 }}>
                {formatConsistency(m.consistency)}
              </div>
            ))}
          </div>

          <RowCaption tone="quiet" style={{ position: 'absolute', left: PLOT_LEFT, top: 218 }}>Your fit</RowCaption>
          <div className={cn(MONTH_GRID, 'absolute')} style={{ ...GRID_GAP, left: PLOT_LEFT, top: 232, width: PLOT_WIDTH, height: 65 }}>
            {chartMonths.map((m) => {
              const isSelected = m.month === selectedMonth;
              const strong = m.suitability !== null && m.suitability >= 8;
              return (
                <button
                  key={m.month}
                  type="button"
                  onClick={() => setSelectedMonth(isSelected ? null : m.month)}
                  aria-pressed={isSelected}
                  aria-label={`${MONTH_ABBR[m.month - 1]} details`}
                  className={cn(
                    'relative flex h-full items-end justify-center rounded-sm transition-colors',
                    isSelected && 'outline outline-2 outline-offset-2 outline-foreground',
                  )}
                  style={{ backgroundColor: 'hsl(var(--foreground) / 0.03)' }}
                >
                  <span
                    className={cn(
                      'flex w-full items-center justify-center rounded-sm font-medium tabular-nums',
                      m.suitability === null
                        ? 'text-muted-foreground'
                        : strong
                          ? 'text-suitability-foreground'
                          : 'text-foreground',
                    )}
                    style={{
                      height: `${fillPct(m.suitability)}%`,
                      fontSize: 14,
                      backgroundColor:
                        m.suitability === null
                          ? 'transparent'
                          : `hsl(var(--suitability) / ${SUITABILITY_OPACITY[stepIndex(m.suitability, SUITABILITY_OPACITY.length)]})`,
                    }}
                  >
                    {m.suitability === null ? '–' : m.suitability}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="absolute" style={{ left: PLOT_LEFT, top: 303, width: PLOT_WIDTH, height: 18 }}>
            {bestWindow && (
              <div
                className="absolute inset-y-0 flex flex-col items-center gap-0.5"
                style={{
                  left: `${((bestWindow.startMonth - 1) / 12) * 100}%`,
                  width: `${((bestWindow.endMonth - bestWindow.startMonth + 1) / 12) * 100}%`,
                }}
              >
                <div className="h-px w-full bg-suitability" />
                <span className="whitespace-nowrap font-semibold uppercase tracking-[0.08em] text-suitability" style={{ fontSize: 9 }}>
                  Best time
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Secondary detail: a quiet inline panel on desktop. Mobile's own Sheet
          is handled entirely in the mobile branch above. */}
      {selected ? (
        <div className="mt-4 border-t border-border pt-3">
          <div className="flex items-baseline justify-between mb-3">
            <h4 className="font-serif text-lg text-foreground">{selected.monthLabel}</h4>
            <button
              type="button"
              onClick={() => setSelectedMonth(null)}
              className="text-xs uppercase tracking-[0.06em] text-muted-foreground hover:text-foreground"
            >
              Close
            </button>
          </div>
          <MonthDetailBody chartMonth={selected} ability={ability} />
        </div>
      ) : null}
    </div>
  );
};

export default SeasonAbilityGraph;
